import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Loader from "../child/Loder";
import DengerAlert from "../child/Alert/DengerAlert";
import { handleUnauthorized } from "../../utils/apiHelper";
import moment from "moment";
import SuccessAlert from "../child/Alert/SuccessAlert";
import { formatAction } from "../../utils/formatAction";
import ChatMessagesTable from "../ChatList/ChatList";
import { reportTypeLabel } from "./reportTypes";

const ViewUserReport = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");
    const [moderatorNote, setModeratorNote] = useState("");
    const [moderatorErrors, setModeratorErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState("");
    const [activeTab, setActiveTab] = useState("overview");
    const authToken = localStorage.getItem("authToken");

    useEffect(() => {
        fetchReport();
    }, [id]);

    const fetchReport = async () => {
        setLoading(true);
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BASE_URL}/admin/user-reports/${id}`,
                { headers: { Authorization: `Bearer ${authToken}` } }
            );

            if (response.status === 401) {
                handleUnauthorized(navigate);
                return;
            }

            const data = await response.json();
            if (!response.ok) throw new Error(data.message || "Failed to fetch report");

            setReport(data.report);
        } catch (err) {
            setErrorMessage(err.message);
            setTimeout(() => setErrorMessage(""), 3000);
        } finally {
            setLoading(false);
        }
    };

    const handleModeratorChange = (e) => {
        setModeratorNote(e.target.value);
        if (moderatorErrors.note) setModeratorErrors({ note: "" });
    };

    const handleModeratorSubmit = async (e) => {
        e.preventDefault();
        if (!moderatorNote.trim()) {
            setModeratorErrors({ note: "Moderator note is required" });
            return;
        }

        try {
            const response = await fetch(
                `${process.env.REACT_APP_BASE_URL}/admin/user-report/${id}/moderator-note`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${authToken}`,
                    },
                    body: JSON.stringify({ note: moderatorNote }),
                }
            );

            const data = await response.json();
            if (!response.ok) throw new Error(data.message || "Failed to add note");

            setReport(data.data);
            setSuccessMessage("Note added successfully");
            setModeratorNote("");
            setTimeout(() => {
                setSuccessMessage("");
                navigate("/user-report");
            }, 500);
        } catch (err) {
            setErrorMessage(err.message);
            setTimeout(() => setErrorMessage(""), 3000);
        }
    };

    const formatText = (str) =>
        str
            ? str
                .replace(/_/g, " ")
                .split(" ")
                .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                .join(" ")
            : "N/A";

    if (loading) return <Loader />;

    return (
        <div className="row gy-4">
            <div className="col-md-12">
                <div className="card h-100">
                    <div className="card-body p-24">
                        {errorMessage && <DengerAlert message={errorMessage} />}
                        {successMessage && <SuccessAlert message={successMessage} />}

                        {/* TABS */}
                        <ul className="nav border-gradient-tab nav-pills mb-20 d-inline-flex">
                            <li className="nav-item">
                                <button className={`nav-link px-24 ${activeTab === "overview" ? "active" : ""}`} onClick={() => setActiveTab("overview")}>
                                    Report Overview
                                </button>
                            </li>
                            <li className="nav-item">
                                <button className={`nav-link px-24 ${activeTab === "reports" ? "active" : ""}`} onClick={() => setActiveTab("reports")}>
                                    Reports & Evidence
                                </button>
                            </li>
                            <li className="nav-item">
                                <button className={`nav-link px-24 ${activeTab === "others" ? "active" : ""}`} onClick={() => setActiveTab("others")}>
                                    Other Reports
                                </button>
                            </li>
                            <li className="nav-item">
                                <button className={`nav-link px-24 ${activeTab === "notes" ? "active" : ""}`} onClick={() => setActiveTab("notes")}>
                                    Moderator Notes
                                </button>
                            </li>
                            {report?.status === "escalated" && (
                                <li className="nav-item">
                                    <button className={`nav-link px-24 ${activeTab === "escalation" ? "active" : ""}`} onClick={() => setActiveTab("escalation")}>
                                        Escalation
                                    </button>
                                </li>
                            )}
                        </ul>

                        <div className="tab-content">

                            {/* OVERVIEW */}
                            <div className={`tab-pane fade ${activeTab === "overview" ? "show active" : ""}`}>
                                {/* ALL YOUR OVERVIEW JSX — unchanged */}
                                {/* (Reporter, status, severity, context, dates, report types) */}
                                <div className="row">
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label fw-bold">Reporter:</label>
                                        <div>
                                            {report?.ownerUserId?.fullName} ({report?.ownerUserId?.email})
                                        </div>
                                    </div>

                                    {/* Reporter Status */}
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label fw-bold">Reporter Status:</label>
                                        <div>
                                            {report?.ownerUserId?.status ? formatText(report.ownerUserId.status) : "N/A"}
                                        </div>
                                    </div>

                                    {/* Reported User */}
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label fw-bold">Reported User:</label>
                                        <div>
                                            {report?.targetUserId?.fullName} ({report?.targetUserId?.email})
                                        </div>
                                    </div>

                                    {/* Reported User Status */}
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label fw-bold">Reported User Status:</label>
                                        <div>
                                            {report?.targetUserId?.status ? formatText(report.targetUserId.status) : "N/A"}
                                        </div>
                                    </div>

                                    <div className="col-md-4 mb-3">
                                        <label className="form-label fw-bold">Reports Against User:</label>
                                        <div>{report?.reportCountAgainstUser}</div>
                                    </div>

                                    {/* Report Status */}
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label fw-bold">Report Status:</label>
                                        <div>{report?.status ? formatText(report.status) : "N/A"}</div>
                                    </div>

                                    {/* Context */}
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label fw-bold">Context:</label>
                                        <div>{report?.context ? formatText(report.context) : "N/A"}</div>
                                    </div>

                                    {/* Severity */}
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label fw-bold">Severity:</label>
                                        <div>{report?.severityTag ? formatText(report.severityTag) : "N/A"}</div>
                                    </div>

                                    {/* BLOCK INFORMATION */}
                                    {report?.isBlock && (
                                        <>
                                            <div className="col-md-4 mb-3">
                                                <label className="form-label fw-bold">Block Information:</label>
                                                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                                                    <div className="fw-semibold text-dark">
                                                        {report?.ownerUserId?.fullName} blocked{" "}
                                                        {report?.targetUserId?.fullName}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-4 mb-3">
                                                <label className="form-label fw-bold">Blocked At:</label>
                                                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                                                    <div>
                                                        {report?.blockedAt ? moment(report.blockedAt).format("DD-MM-YYYY, hh:mm A") : "N/A"}

                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                    {/* Created At */}
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label fw-bold">Created At:</label>
                                        <div>
                                            {report?.createdAt ? moment(report.createdAt).format("DD-MM-YYYY, hh:mm A") : "N/A"}
                                        </div>
                                    </div>

                                    {/* Updated At */}
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label fw-bold">Updated At:</label>
                                        <div>
                                            {report?.updatedAt ? moment(report.updatedAt).format("DD-MM-YYYY, hh:mm A") : "N/A"}
                                        </div>
                                    </div>

                                    {/* Report Types (Badges) */}
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label fw-bold">Report Types:</label>
                                        <div className="d-flex flex-wrap gap-1">
                                            {report?.reportTypes?.length ? (
                                                [...new Set(report.reportTypes)].map((type, idx) => (
                                                    <span key={idx} className="badge bg-danger-subtle text-danger">
                                                        {reportTypeLabel(type)}
                                                    </span>
                                                ))
                                            ) : (
                                                <span>N/A</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-12 mt-10 d-flex justify-content-end gap-2 flex-wrap">

                                        <button
                                            className="btn btn-primary"
                                            type="button"
                                            onClick={() =>
                                                navigate(`/user/view-user/${report.ownerUserId._id}`)
                                            }
                                        >
                                            View {report.ownerUserId.fullName}'s Profile
                                        </button>

                                        <button
                                            className="btn btn-primary"
                                            type="button"
                                            onClick={() =>
                                                navigate(`/user/view-user/${report.targetUserId._id}`)
                                            }
                                        >
                                            View {report.targetUserId.fullName}'s Profile
                                        </button>

                                        <button
                                            className="btn btn-primary"
                                            type="button"
                                            onClick={() =>
                                                navigate("/chat", {
                                                    state: {
                                                        uid: `cm_${report.ownerUserId._id}`,
                                                        otherUser: `cm_${report.targetUserId._id}`,
                                                    },
                                                })
                                            }
                                        >
                                            View Chat
                                        </button>

                                    </div>
                                </div>

                            </div>

                            {/* REPORTS */}
                            <div className={`tab-pane fade ${activeTab === "reports" ? "show active" : ""}`}>
                                {/* YOUR REPORTS + EVIDENCE ACCORDION — unchanged */}
                                <div className="col-md-12 mb-3">
                                    {report?.reports?.length ? (
                                        <div className="row g-3">
                                            {report.reports.map((r, idx) => (
                                                <div className="col-md-6" key={r._id}>
                                                    <div className="accordion" id={`reportAccordion-${idx}`}>
                                                        <div className="accordion-item">
                                                            <h2 className="accordion-header">
                                                                <button
                                                                    className="accordion-button collapsed py-2 fw-semibold"
                                                                    type="button"
                                                                    data-bs-toggle="collapse"
                                                                    data-bs-target={`#report-${idx}`}
                                                                    aria-expanded="false"
                                                                    aria-controls="collapseNotes"
                                                                >
                                                                    {reportTypeLabel(r.type)}
                                                                </button>
                                                            </h2>

                                                            <div
                                                                id={`report-${idx}`}
                                                                className="accordion-collapse collapse"
                                                            >
                                                                <div className="accordion-body">
                                                                    <div className="row gy-3">
                                                                        {/* Description */}
                                                                        <div className="col-md-5">
                                                                            <label className="fw-bold mb-1">Description</label>
                                                                            <div className="text-muted">
                                                                                {r.description || "N/A"}
                                                                            </div>
                                                                        </div>

                                                                        {/* Evidence */}
                                                                        <div className="col-md-7">
                                                                            <label className="fw-bold mb-2">Evidence</label>

                                                                            {r.evidence?.length ? (
                                                                                <div className="row g-2">
                                                                                    {r.evidence.map((e) => (
                                                                                        <div key={e._id} className="col-6 col-md-4">
                                                                                            <div
                                                                                                className="card border h-100 cursor-pointer"
                                                                                                onClick={() =>
                                                                                                    window.open(e.url, "_blank", "noopener,noreferrer")
                                                                                                }
                                                                                            >
                                                                                                <div className="card-body p-1">
                                                                                                    {e.fileType === "image" && (
                                                                                                        <img
                                                                                                            src={e.url}
                                                                                                            alt="Evidence"
                                                                                                            className="img-fluid rounded"
                                                                                                            style={{
                                                                                                                height: "140px",
                                                                                                                objectFit: "cover",
                                                                                                                width: "100%",
                                                                                                            }}
                                                                                                        />
                                                                                                    )}

                                                                                                    {e.fileType === "video" && (
                                                                                                        <video
                                                                                                            src={e.url}
                                                                                                            controls
                                                                                                            className="w-100 rounded"
                                                                                                            style={{ height: "140px" }}
                                                                                                        />
                                                                                                    )}

                                                                                                    {e.fileType !== "image" &&
                                                                                                        e.fileType !== "video" && (
                                                                                                            <div className="text-center p-2 text-primary">
                                                                                                                Download File
                                                                                                            </div>
                                                                                                        )}
                                                                                                </div>
                                                                                            </div>
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            ) : (
                                                                                <div className="text-muted">No evidence provided</div>
                                                                            )}
                                                                        </div>

                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <span>N/A</span>
                                    )}

                                    <div className="row">
                                        <div className="col-md-12 mt-20 d-flex justify-content-end gap-2 flex-wrap">

                                            <button
                                                className="btn btn-primary"
                                                type="button"
                                                onClick={() =>
                                                    navigate(`/user/view-user/${report.ownerUserId._id}`)
                                                }
                                            >
                                                View {report.ownerUserId.fullName}'s Profile
                                            </button>

                                            <button
                                                className="btn btn-primary"
                                                type="button"
                                                onClick={() =>
                                                    navigate(`/user/view-user/${report.targetUserId._id}`)
                                                }
                                            >
                                                View {report.targetUserId.fullName}'s Profile
                                            </button>

                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* OTHER REPORTS */}
                            <div className={`tab-pane fade ${activeTab === "others" ? "show active" : ""}`}>
                                {/* YOUR OTHER REPORTS LIST — unchanged */}
                                <div className="col-md-12 mb-3">
                                    {report?.otherReportsAgainstUser?.length ? (

                                        <div className="card border-0 shadow-sm">
                                            <div className="card-header bg-light fw-semibold mb-2">
                                                Other Reports Against This User
                                                <span className="badge bg-secondary ms-2 ml-2">
                                                    {report.otherReportsAgainstUser.length}
                                                </span>
                                            </div>

                                            <ul className="list-group list-group-flush">
                                                {report.otherReportsAgainstUser.map((r) => (
                                                    <li
                                                        key={r._id}
                                                        className="list-group-item d-flex justify-content-between align-items-center"
                                                    >
                                                        <div>
                                                            <div className="fw-medium">
                                                                {r.ownerUserId?.fullName || "Unknown User"}
                                                            </div>
                                                            <small className="text-muted">
                                                                {r.ownerUserId?.email}
                                                            </small>
                                                        </div>

                                                        <button
                                                            className="btn btn-sm btn-outline-primary"
                                                            onClick={() => navigate(`/user-report/view/${r._id}`)}
                                                        >
                                                            View
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                    ) : (
                                        <span>No other reports</span>
                                    )}
                                </div>
                                <div className="row">
                                    <div className="col-md-12 mt-10 d-flex justify-content-end gap-2 flex-wrap">

                                        <button
                                            className="btn btn-primary"
                                            type="button"
                                            onClick={() =>
                                                navigate(`/user/view-user/${report.ownerUserId._id}`)
                                            }
                                        >
                                            View {report.ownerUserId.fullName}'s Profile
                                        </button>

                                        <button
                                            className="btn btn-primary"
                                            type="button"
                                            onClick={() =>
                                                navigate(`/user/view-user/${report.targetUserId._id}`)
                                            }
                                        >
                                            View {report.targetUserId.fullName}'s Profile
                                        </button>

                                    </div>
                                </div>
                            </div>

                            {/* MODERATOR NOTES */}
                            <div className={`tab-pane fade ${activeTab === "notes" ? "show active" : ""}`}>
                                {/* YOUR MODERATOR NOTES + FORM — unchanged */}
                                <div className="col-md-12 mt-4">
                                    <label className="form-label fw-bold">Moderator Notes</label>

                                    <div className="accordion" id="moderatorNotesAccordion">
                                        <div className="accordion-item">
                                            <h2 className="accordion-header" id="headingNotes">
                                                <button
                                                    className="accordion-button collapsed"
                                                    type="button"
                                                    data-bs-toggle="collapse"
                                                    data-bs-target="#collapseNotes"
                                                    aria-expanded="false"
                                                    aria-controls="collapseNotes"
                                                >
                                                    Moderator Notes ({report?.moderatorNotes?.length || 0})
                                                </button>
                                            </h2>

                                            <div
                                                id="collapseNotes"
                                                className="accordion-collapse collapse"
                                                aria-labelledby="headingNotes"
                                                data-bs-parent="#moderatorNotesAccordion"
                                            >
                                                <div className="accordion-body p-3">
                                                    {report?.moderatorNotes?.length ? (
                                                        <div className="d-flex flex-column gap-3">
                                                            {[...report.moderatorNotes]
                                                                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                                                                .map((note) => (
                                                                    <div
                                                                        key={note._id}
                                                                        className="border rounded p-3 bg-light-subtle"
                                                                    >
                                                                        <div className="d-flex justify-content-between align-items-start">
                                                                            <div className="fw-semibold">
                                                                                {formatAction(note.note)}
                                                                            </div>
                                                                            <small className="text-muted text-nowrap">
                                                                                {moment(note.createdAt).format("DD MMM YYYY, hh:mm A")}
                                                                            </small>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                        </div>
                                                    ) : (
                                                        <div className="text-muted text-center py-3">
                                                            No moderator notes yet.
                                                        </div>
                                                    )}
                                                </div>

                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {/* Moderator Note Form */}
                                <div className="row mt-4">
                                    <form onSubmit={handleModeratorSubmit} className="row gy-3">
                                        <div className="col-md-12">
                                            <label className="form-label">Moderator Note</label>
                                            <textarea
                                                name="moderatorNote"
                                                className="form-control"
                                                placeholder="Enter Moderator Note"
                                                value={moderatorNote}
                                                onChange={handleModeratorChange}
                                                onInput={(e) => {
                                                    e.target.value = e.target.value
                                                        .replace(/\s{2,}/g, " ")
                                                        .trimStart();
                                                }}
                                            />
                                            {moderatorErrors.note && (
                                                <div className="text-danger mt-1">{moderatorErrors.note}</div>
                                            )}
                                        </div>
                                        <div className="col-12 text-end">
                                            <button className="btn btn-primary" type="submit">
                                                Save Moderator Note
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>

                            {/* ESCALATION */}
                            <div className={`tab-pane fade ${activeTab === "escalation" ? "show active" : ""}`}>
                                {/* YOUR ESCALATION DETAILS — unchanged */}
                                <div className="row">
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label fw-bold">Report Status:</label>
                                        <div>{report?.status ? formatText(report.status) : "N/A"}</div>
                                    </div>


                                    {report?.status === "escalated" && report?.escalation && (
                                        <>
                                            <div className="col-md-4 mb-3">
                                                <label className="form-label fw-bold">Escalated By:</label>
                                                <div>{report.escalation.escalatedBy?.name}({report.escalation.escalatedBy?.email})</div>
                                            </div>

                                            <div className="col-md-4 mb-3">
                                                <label className="form-label fw-bold">Escalated At:</label>
                                                <div>
                                                    {moment(report.escalation.escalatedAt).format(
                                                        "DD-MM-YYYY, hh:mm A"
                                                    )}
                                                </div>
                                            </div>

                                            <div className="col-md-4 mb-3">
                                                <label className="form-label fw-bold">Escalation Reason:</label>
                                                <div className="text-muted">
                                                    {report.escalation.reason || "N/A"}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ViewUserReport;
